// C:\Users\Caner\inosel-panel\ortak\src\fatura-takip\src\pages\FaturaDuzenle.js
import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

const FaturaDuzenle = () => {
  // 🔎 Arama alanları
  const [faturaNo, setFaturaNo] = useState("");
  const [firmaAdiArama, setFirmaAdiArama] = useState(""); // opsiyonel autocomplete
  const [tarihArama, setTarihArama] = useState(""); // opsiyonel date (YYYY-MM-DD)

  // 📄 Fatura içeriği
  const [satirlar, setSatirlar] = useState([]);
  const [faturaId, setFaturaId] = useState(null);
  const [createdBy, setCreatedBy] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [faturaDocRef, setFaturaDocRef] = useState(null);

  // 🔧 Yardımcı listeler
  const [firmalar, setFirmalar] = useState([]);
  const [plakalar, setPlakalar] = useState([]);
  const [urunler, setUrunler] = useState([]);

  // 🧭 UI durumları
  const [guncelleButonuAktif, setGuncelleButonuAktif] = useState(false);
  const [secilebilirFaturalar, setSecilebilirFaturalar] = useState([]); // çoklu sonuç için

  // ────────────────────────────────────────────────────────────────────────────────
  // Veri yükleme
  useEffect(() => {
    const fetchData = async () => {
      const [firmaSnap, plakaSnap, faturaSnap] = await Promise.all([
        getDocs(collection(db, "firms")),
        getDocs(collection(db, "plates")),
        getDocs(collection(db, "faturalar")),
      ]);

      const firmaList = firmaSnap.docs.map((d) => ({
        id: d.id,
        adi: d.data().firmaAdi,
        kdvOrani: d.data().kdvOrani ?? 18,
      }));
      setFirmalar(firmaList);

      setPlakalar(
        plakaSnap.docs.map((d) => (d.data().plate || "").toString().trim())
      );

      const eskiUrunler = faturaSnap.docs.flatMap((d) =>
        (d.data().satirlar || []).map((s) => s.urunAdi)
      );
      setUrunler([...new Set(eskiUrunler)].sort());
    };

    fetchData();

    // sayfadan çıkarken faturaAcik bayrağını bırak
    return () => {
      if (faturaDocRef) {
        updateDoc(faturaDocRef, { faturaAcik: "" });
      }
    };
  }, [faturaDocRef]);

  // ────────────────────────────────────────────────────────────────────────────────
  // Yardımcılar
  const format = (num) => new Intl.NumberFormat("tr-TR").format(Number(num || 0).toFixed(2));
  const firmaBul = (adi) => firmalar.find((f) => f.adi === adi);

  const satirToplam = satirlar.reduce(
    (acc, s) => {
      const adet = parseFloat(s.adet || 0);
      const fiyat = parseFloat(s.fiyat || 0);
      const iskonto = parseFloat(s.iskonto || 0);
      const tutar = adet * fiyat;
      const iskontoTutar = tutar * (iskonto / 100);
      const net = tutar - iskontoTutar;
      acc.tutar += tutar;
      acc.iskonto += iskontoTutar;
      acc.net += net;
      return acc;
    },
    { tutar: 0, iskonto: 0, net: 0 }
  );

  // ────────────────────────────────────────────────────────────────────────────────
  // Fatura getir (Fatura No zorunlu, Firma/Tarih opsiyonel filtre)
  const faturaGetir = async () => {
    const trimmedFaturaNo = (faturaNo || "").trim();
    const regex = /^[a-zA-Z0-9\s-_/\\]+$/;

    if (!trimmedFaturaNo || !regex.test(trimmedFaturaNo) || trimmedFaturaNo.length > 50) {
      alert("Girdiğiniz fatura numarası uygun değil.");
      return;
    }

    // Firestore sorgusu: sadece zorunlu olanlarla (index gerektirmesin diye)
    const q = query(
      collection(db, "faturalar"),
      where("faturaNo", "==", trimmedFaturaNo),
      where("deleted", "==", false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      alert("Fatura bulunamadı veya silinmiş.");
      return;
    }

    // İsteğe bağlı filtreler (client-side)
    let docs = snapshot.docs;
    // Firma adı girildiyse filtrele
    const firmaTrim = (firmaAdiArama || "").trim();
    if (firmaTrim) {
      docs = docs.filter((d) => (d.data().firmaAdi || "").toString().trim() === firmaTrim);
    }
    // Tarih girildiyse filtrele — YYYY-MM-DD karşılaştır (toDate().toISOString().slice(0,10))
    const tarihTrim = (tarihArama || "").trim();
    if (tarihTrim) {
      docs = docs.filter((d) => {
        const t = d.data().tarih;
        const iso = t?.toDate ? t.toDate().toISOString().slice(0, 10) : "";
        return iso === tarihTrim;
      });
    }

    if (docs.length === 0) {
      alert("Filtrelere uygun fatura bulunamadı.");
      return;
    }

    // Eğer birden çok eşleşme varsa seçtir
    if (docs.length > 1) {
      // Küçük bir seçim listesi gösterelim
      setSecilebilirFaturalar(
        docs.map((d) => ({
          id: d.id,
          ref: d.ref,
          ...d.data(),
        }))
      );
      // içeriği temizleyelim; kullanıcı birini seçince dolduracağız
      setSatirlar([]);
      setFaturaId(null);
      setFaturaDocRef(null);
      return;
    }

    // Tek sonuç → direkt yükle
    await faturaYukle(docs[0]);
  };

  const faturaYukle = async (docSnap) => {
    const docRef = docSnap.ref;
    const data = docSnap.data();
    const docId = docSnap.id;

    // faturaAcik kilidi
    if (data.faturaAcik && data.faturaAcik !== auth.currentUser?.email) {
      alert(`${data.faturaAcik} kullanıcısı faturada işlem yaptığı için çağrılamaz.`);
      return;
    }

    await updateDoc(docRef, { faturaAcik: auth.currentUser?.email || "bilinmiyor" });
    setFaturaDocRef(docRef);
    setFaturaId(docId);
    setCreatedBy(data.createdBy);
    setCreatedAt(data.createdAt);

    const sabit = {
      faturaNo: data.faturaNo,
      firmaAdi: data.firmaAdi,
      firmaId: data.firmaId,
      tarih: data.tarih?.toDate().toISOString().split("T")[0] || "",
    };

    const yeniSatirlar = (data.satirlar || []).map((s) => ({
      ...sabit,
      ...s,
    }));

    setSatirlar(yeniSatirlar);
    setSecilebilirFaturalar([]); // seçim listesini kapat
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Satır düzenleme
  const handleChange = (i, key, value) => {
    const yeni = [...satirlar];

    if (key === "urunAdi") value = value.toUpperCase();

    yeni[i][key] = value;

    if (key === "firmaAdi") {
      const secilen = firmaBul(value);
      yeni.forEach((s) => {
        s.firmaAdi = value;
        s.firmaId = secilen?.id || "";
        s.kdv = secilen?.kdvOrani ?? "";
      });
    }

    if (["faturaNo", "firmaAdi", "tarih"].includes(key)) {
      yeni.forEach((s) => (s[key] = value));
    }

    setSatirlar(yeni);
    setGuncelleButonuAktif(true);
  };

  const handleYeniSatir = () => {
    if (satirlar.length === 0) return;
    const son = satirlar[0];
    setSatirlar([
      ...satirlar,
      {
        faturaNo: son.faturaNo,
        firmaAdi: son.firmaAdi,
        firmaId: son.firmaId,
        tarih: son.tarih,
        plaka: "",
        urunAdi: "",
        fiyat: "",
        adet: 1,
        iskonto: "",
        kdv: son.kdv,
        aciklama: "",
      },
    ]);
    setGuncelleButonuAktif(true);
  };

  const handleSil = (i) => {
    setSatirlar((prev) => prev.filter((_, index) => index !== i));
    setGuncelleButonuAktif(true);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Fatura sil / güncelle
  const handleFaturaSil = async () => {
    if (!faturaId) return;

    await updateDoc(doc(db, "faturalar", faturaId), {
      deleted: true,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "bilinmiyor",
      faturaAcik: "",
    });

    alert("Fatura başarıyla silindi ✅");
    setGuncelleButonuAktif(false);
    setSatirlar([]);
    setFaturaId(null);
    setFaturaDocRef(null);
  };

  const handleGuncelle = async () => {
    if (!faturaId) return;

    const aktifSatirlar = satirlar.filter(
      (s) => s.urunAdi && s.fiyat && s.adet > 0 && s.kdv !== "" && s.firmaId
    );
    if (!aktifSatirlar.length) {
      alert("Güncellenecek geçerli satır yok.");
      return;
    }

    const ilk = aktifSatirlar[0];
    await updateDoc(doc(db, "faturalar", faturaId), {
      faturaNo: ilk.faturaNo,
      firmaAdi: ilk.firmaAdi,
      firmaId: ilk.firmaId,
      tarih: ilk.tarih ? new Date(ilk.tarih) : null, // tarih opsiyonel
      satirlar: aktifSatirlar,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.email || "bilinmiyor",
      createdBy,
      createdAt,
      faturaAcik: "",
    });

    alert("Fatura başarıyla güncellendi ✅");
    setGuncelleButonuAktif(false);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Render
  return (
    <div style={{ padding: 20, overflowX: "auto" }}>
      <h2 style={{ textAlign: "center", color: "#334155", fontSize: 22, fontWeight: "bold", marginBottom: 20 }}>
        Fatura Düzenle
      </h2>

      {/* Arama Alanları */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input
          style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4, width: 200 }}
          placeholder="Fatura No (zorunlu)"
          value={faturaNo}
          onChange={(e) => setFaturaNo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && faturaGetir()}
        />
        <input
          style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4, width: 240 }}
          placeholder="Firma Adı (opsiyonel)"
          list="firmalar" // autocomplete
          value={firmaAdiArama}
          onChange={(e) => setFirmaAdiArama(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && faturaGetir()}
        />
        <input
          type="date"
          style={{ padding: 6, border: "1px solid #ccc", borderRadius: 4, width: 170 }}
          value={tarihArama}
          onChange={(e) => setTarihArama(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && faturaGetir()}
          title="Tarih (opsiyonel)"
        />
        <button
          onClick={faturaGetir}
          style={{ backgroundColor: "#2563eb", color: "white", padding: "6px 16px", borderRadius: 4, border: "none" }}
        >
          Getir
        </button>
      </div>

      {/* Eğer birden çok fatura eşleştiyse seçim listesi */}
      {secilebilirFaturalar.length > 0 && (
        <div style={{ marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 6, padding: 10, background: "#fff" }}>
          <div style={{ fontWeight: "bold", marginBottom: 8 }}>
            Birden fazla fatura bulundu. Lütfen seçin:
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
            <thead style={{ background: "#f1f5f9" }}>
              <tr>
                {["Firma", "Fatura No", "Tarih", "Oluşturan", "Seç"].map((h) => (
                  <th key={h} style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {secilebilirFaturalar.map((f) => {
                const tarihStr = f.tarih?.toDate ? f.tarih.toDate().toISOString().slice(0,10) : "";
                return (
                  <tr key={f.id}>
                    <td style={{ border: "1px solid #eee", padding: 6, textAlign: "center" }}>{f.firmaAdi}</td>
                    <td style={{ border: "1px solid #eee", padding: 6, textAlign: "center" }}>{f.faturaNo}</td>
                    <td style={{ border: "1px solid #eee", padding: 6, textAlign: "center" }}>{tarihStr}</td>
                    <td style={{ border: "1px solid #eee", padding: 6, textAlign: "center" }}>{f.createdBy || "-"}</td>
                    <td style={{ border: "1px solid #eee", padding: 6, textAlign: "center" }}>
                      <button
                        onClick={() => faturaYukle({ id: f.id, ref: f.ref, data: () => f })}
                        style={{ backgroundColor: "#16a34a", color: "white", padding: "4px 10px", borderRadius: 4, border: "none" }}
                      >
                        Seç
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Düzenleme Tablosu */}
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1200px", fontSize: "13px" }}>
        <thead style={{ backgroundColor: "#e2e8f0" }}>
          <tr>
            {"Fatura No,Firma,Tarih,Plaka,Ürün Adı,Fiyat,Adet,KDV,İskonto %,Açıklama,Sil"
              .split(",")
              .map((h, i) => (
                <th key={i} style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>{h}</th>
              ))}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s, i) => (
            <tr key={i}>
              {["faturaNo", "firmaAdi", "tarih", "plaka", "urunAdi", "fiyat", "adet", "kdv", "iskonto", "aciklama"].map((key) => (
                <td key={key} style={{ border: "1px solid #ccc", padding: "4px" }}>
                  <input
                    type={key === "tarih" ? "date" : key === "adet" || key === "fiyat" || key === "kdv" ? "number" : "text"}
                    value={s[key]}
                    onChange={(e) => handleChange(i, key, e.target.value)}
                    style={{
                      width: "100%",
                      border: "none",
                      outline: "none",
                      fontSize: "12px",
                      ...(key === "urunAdi" && { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" })
                    }}
                    list={
                      key === "firmaAdi" ? "firmalar" :
                      key === "plaka" ? "plakalar" :
                      key === "urunAdi" ? "urunler" : undefined
                    }
                    disabled={i > 0 && ["faturaNo", "firmaAdi", "tarih"].includes(key)}
                    min={key === "adet" ? 1 : undefined}
                    title={key === "urunAdi" ? s[key] : ""}
                  />
                </td>
              ))}
              <td style={{ border: "1px solid #ccc", textAlign: "center" }}>
                <button
                  onClick={() => handleSil(i)}
                  style={{ backgroundColor: "#dc2626", color: "white", padding: "2px 8px", fontSize: "11px", borderRadius: 4 }}
                  disabled={!faturaId}
                >
                  Sil
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 10, fontSize: "13px", fontWeight: "bold" }}>
        Toplam Tutar: {format(satirToplam.tutar)} ₺ | İskonto: {format(satirToplam.iskonto)} ₺ | Net Tutar: {format(satirToplam.net)} ₺
      </div>

      {/* Autocomplete datalist'ler */}
      <datalist id="firmalar">{firmalar.map((f, i) => <option key={i} value={f.adi} />)}</datalist>
      <datalist id="plakalar">{plakalar.map((p, i) => <option key={i} value={p} />)}</datalist>
      <datalist id="urunler">{urunler.map((u, i) => <option key={i} value={u} />)}</datalist>

      <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
        <button
          onClick={handleYeniSatir}
          style={{ backgroundColor: "#f59e0b", color: "white", padding: "8px 16px", borderRadius: 4, border: "none" }}
          disabled={!faturaId}
        >
          + Satır Ekle
        </button>
        <button
          onClick={handleGuncelle}
          style={{ backgroundColor: guncelleButonuAktif ? "#16a34a" : "#9ca3af", color: "white", padding: "8px 16px", borderRadius: 4, border: "none" }}
          disabled={!guncelleButonuAktif || !faturaId}
        >
          Güncelle
        </button>
        <button
          onClick={handleFaturaSil}
          style={{ backgroundColor: "#dc2626", color: "white", padding: "8px 16px", borderRadius: 4, border: "none" }}
          disabled={!faturaId}
        >
          Sil
        </button>
      </div>
    </div>
  );
};

export default FaturaDuzenle;
